import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";
import notice from "@/lib/consentNotice.json";

import { hasSensitiveConsent } from "@/lib/sensitiveConsent";
export { hasSensitiveConsent } from "@/lib/sensitiveConsent";

function Choice({ group, profile, onSaved, busy, setBusy }) {
  const { lang } = useI18n();
  const text = notice.groups[group][lang];
  const [processing, setProcessing] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    setProcessing(hasSensitiveConsent(profile, group));
    setSharing(hasSensitiveConsent(profile, group, "sharing"));
  }, [profile, group]);
  const save = async () => {
    setBusy(true); setMessage("");
    try {
      const { data } = await api.patch(`/profile/consents/${group}`, {
        version: notice.version, processing, sharing,
        language: lang, revision: profile.consent_revision || 0,
      });
      await onSaved(data.profile);
      setMessage(lang === "it" ? "Scelte salvate." : "Choices saved.");
    } catch (error) {
      setMessage(error?.response?.status === 409
        ? (lang === "it" ? "Le scelte sono cambiate in un’altra pagina. Ricarica prima di riprovare." : "Choices changed in another page. Reload before trying again.")
        : (lang === "it" ? "Salvataggio non riuscito. Le scelte precedenti restano in vigore." : "Could not save. Your previous choices remain in effect."));
    } finally { setBusy(false); }
  };
  return <fieldset disabled={busy} className="border rounded-md p-4 space-y-3 min-w-0" data-testid={`consent-${group}`}>
    <legend className="px-1 font-medium">{text.title}</legend>
    <label className="flex items-start gap-3 text-sm">
      <input type="checkbox" checked={processing} onChange={e => { setProcessing(e.target.checked); if (!e.target.checked) setSharing(false); }} className="mt-1 shrink-0" data-testid={`consent-${group}-processing`} />
      <span>{text.processing}</span>
    </label>
    <label className="flex items-start gap-3 text-sm">
      <input type="checkbox" checked={sharing} disabled={!processing || busy} onChange={e => setSharing(e.target.checked)} className="mt-1 shrink-0" data-testid={`consent-${group}-sharing`} />
      <span>{text.sharing}</span>
    </label>
    <p className="text-xs">{lang === "it"
      ? "Puoi lasciare entrambe le scelte disattivate. Salvando senza il primo consenso, elimini i dati di questa categoria dai campi del profilo. La sola revoca della visibilità li nasconde agli altri. Le copie dei messaggi o i testi liberi vanno gestiti separatamente."
      : "You may leave both choices off. Saving without the first consent removes this category’s structured profile data. Withdrawing sharing alone hides it from others. Messages and free text must be managed separately."}</p>
    <button type="button" className="nvl-btn nvl-btn-outline" onClick={save} data-testid={`consent-${group}-save`}>{lang === "it" ? "Salva scelte" : "Save choices"}</button>
    <p role="status" className="text-sm">{message}</p>
  </fieldset>;
}

export default function SensitiveConsents({ profile, onSaved }) {
  const { lang } = useI18n();
  const [busy, setBusy] = useState(false);
  if (!profile) return null;
  return <section id="sensitive-consents" className="nvl-card p-4 sm:p-6 my-6 space-y-4" data-testid="sensitive-consents">
    <h2 className="text-2xl">{lang === "it" ? "Le tue scelte sui dati delicati" : "Your sensitive-data choices"}</h2>
    <p className="text-sm">{lang === "it"
      ? "GD8 srls usa queste informazioni solo con il tuo consenso esplicito. Puoi usare il servizio senza compilare questi campi e cambiare le scelte qui o in Impostazioni → Privacy e dati. La revoca non modifica la liceità degli utilizzi precedenti."
      : "GD8 srls uses this information only with your explicit consent. You can use the service without filling in these fields and change your choices here or in Settings → Privacy and data. Withdrawal does not affect the lawfulness of earlier use."}</p>
    <p className="text-sm">{lang === "it"
      ? "Le informazioni già presenti senza un consenso registrato non vengono utilizzate nella ricerca o mostrate agli altri. Scegli se autorizzarle oppure eliminarle. Non inserire dati delicati nella biografia, nelle foto o nei messaggi per aggirare queste scelte."
      : "Existing information without recorded consent is not used in search or shown to others. Choose whether to authorise it or remove it. Do not use your bio, photos or messages to bypass these choices."}</p>
    <Link to="/legal/privacy" target="_blank" rel="noreferrer" className="underline text-sm">{lang === "it" ? "Leggi l’informativa privacy" : "Read the privacy notice"}</Link>
    {Object.keys(notice.groups).map(group => <Choice key={group} group={group} profile={profile} onSaved={onSaved} busy={busy} setBusy={setBusy} />)}
    <p className="text-xs">{lang === "it" ? "Contatto" : "Contact"}: info@gd8.it</p>
  </section>;
}

export function SensitiveField({ profile, group, purpose = "processing", children }) {
  const { lang } = useI18n();
  const enabled = hasSensitiveConsent(profile, group, purpose);
  return <div>
    {!enabled && <p className="text-xs my-2">{lang === "it" ? "Campo facoltativo: per usarlo, scegli il consenso corrispondente nella sezione sopra." : "Optional field: to use it, choose the corresponding consent in the section above."}</p>}
    <fieldset disabled={!enabled} className="min-w-0 disabled:opacity-50">{children}</fieldset>
  </div>;
}
